import type { FC, ReactNode } from 'react'

import { useEffect, useMemo, useRef } from 'react'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

type Point = [number, number]

interface FloorPlanProps {
    stop: number
    room: string
    className?: string
}

const ROOMS = [
    { label: 'Bedroom', x: 8, y: 8, width: 84, height: 74 },
    { label: 'Living room', x: 98, y: 8, width: 134, height: 88 },
    { label: 'Hall', x: 8, y: 88, width: 118, height: 64 },
    { label: 'Kitchen', x: 132, y: 102, width: 100, height: 50 }
]

const ROUTE: Array<Point> = [
    [206, 26],
    [180, 70],
    [112, 92],
    [70, 94],
    [50, 85],
    [50, 42],
    [50, 85],
    [90, 120],
    [129, 127],
    [194, 128],
    [180, 99],
    [150, 64]
]

const STOPS = [0, 5, 9, 11]

const DOCK: Point = [22, 138]

const segmentLengths = ROUTE.slice(1).map((point, index) => Math.hypot(point[0] - ROUTE[index][0], point[1] - ROUTE[index][1]))

const cumulative = segmentLengths.reduce<Array<number>>((totals, length) => [...totals, totals[totals.length - 1] + length], [0])

const TOTAL = cumulative[cumulative.length - 1]

const pointAt = (distance: number): Point => {
    const clamped = Math.min(TOTAL, Math.max(0, distance))

    for (let index = 0; index < segmentLengths.length; index++) {
        if (clamped <= cumulative[index + 1]) {
            const local = (clamped - cumulative[index]) / (segmentLengths[index] || 1)
            const [x1, y1] = ROUTE[index]
            const [x2, y2] = ROUTE[index + 1]

            return [x1 + (x2 - x1) * local, y1 + (y2 - y1) * local]
        }
    }

    return ROUTE[ROUTE.length - 1]
}

const FloorPlan: FC<FloorPlanProps> = ({ stop, room, className }): ReactNode => {
    const markerRef = useRef<SVGGElement>(null)
    const trailRef = useRef<SVGPolylineElement>(null)
    const distanceRef = useRef(0)
    const reducedMotion = useReducedMotion()
    const routePoints = useMemo(() => ROUTE.map(point => point.join(',')).join(' '), [])

    useEffect(() => {
        const target = cumulative[STOPS[Math.min(stop, STOPS.length - 1)]]
        const from = distanceRef.current

        const paint = (distance: number) => {
            const [x, y] = pointAt(distance)

            distanceRef.current = distance
            markerRef.current?.setAttribute('transform', `translate(${x.toFixed(2)} ${y.toFixed(2)})`)
            trailRef.current?.setAttribute('stroke-dasharray', `${distance.toFixed(2)} ${TOTAL.toFixed(2)}`)
        }

        if (reducedMotion || from === target) {
            paint(target)

            return
        }

        let frame = 0
        const start = performance.now()
        const duration = Math.min(1800, 500 + Math.abs(target - from) * 4)

        const tick = (now: number) => {
            const progress = Math.min(1, (now - start) / duration)
            const eased = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2

            paint(from + (target - from) * eased)

            if (progress < 1) frame = requestAnimationFrame(tick)
        }

        frame = requestAnimationFrame(tick)

        return () => cancelAnimationFrame(frame)
    }, [stop, reducedMotion])

    return (
        <svg
            viewBox='0 0 240 160'
            role='img'
            aria-label={`Floor plan of the home. Oriel is in the ${room.toLowerCase()}.`}
            className={className}
        >
            {ROOMS.map(area => (
                <g key={area.label}>
                    <rect
                        x={area.x}
                        y={area.y}
                        width={area.width}
                        height={area.height}
                        rx='3'
                        className='fill-white/[0.03] stroke-white/20'
                        strokeWidth='0.8'
                    />

                    <text
                        x={area.x + 5}
                        y={area.y + 10}
                        className='fill-white/45 font-mono uppercase'
                        fontSize='5.6'
                        letterSpacing='0.6'
                    >
                        {area.label}
                    </text>
                </g>
            ))}

            <line x1='150' y1='8' x2='222' y2='8' className='stroke-ember/70' strokeWidth='1.6' strokeLinecap='round' />
            <rect x='22' y='24' width='40' height='30' rx='2' className='fill-white/[0.06]' />
            <rect x='118' y='52' width='56' height='14' rx='3' className='fill-white/[0.06]' />
            <rect x='176' y='136' width='40' height='10' rx='1.5' className='fill-white/[0.06]' />

            <g transform={`translate(${DOCK[0]} ${DOCK[1]})`}>
                <rect x='-6' y='-4' width='12' height='8' rx='2' className='fill-none stroke-white/40' strokeWidth='0.8' />
                <text x='10' y='2' className='fill-white/45 font-mono uppercase' fontSize='5' letterSpacing='0.5'>
                    Dock
                </text>
            </g>

            <polyline
                points={routePoints}
                fill='none'
                className='stroke-white/15'
                strokeWidth='0.9'
                strokeDasharray='2 2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
            />

            <polyline
                ref={trailRef}
                points={routePoints}
                fill='none'
                className='stroke-ember'
                strokeWidth='1.4'
                strokeDasharray={`0 ${TOTAL}`}
                strokeLinecap='round'
                strokeLinejoin='round'
            />

            {STOPS.map((index, stopIndex) => (
                <circle
                    key={index}
                    cx={ROUTE[index][0]}
                    cy={ROUTE[index][1]}
                    r='1.8'
                    className={stopIndex <= stop ? 'fill-ember' : 'fill-white/30'}
                />
            ))}

            <g ref={markerRef} transform={`translate(${ROUTE[0][0]} ${ROUTE[0][1]})`}>
                <circle r='9' className='fill-ember/15' />
                <circle r='4.6' className='fill-ink stroke-ember' strokeWidth='1.4' />
                <circle r='1.7' className='fill-ember' />
            </g>
        </svg>
    )
}

export default FloorPlan
